import React, { useState, useMemo, useRef } from 'react'
import { toast } from 'react-toastify'
import { Link } from 'react-router-dom'
import { InboxType } from '../../../../server/models/Inbox'

// components
import { Skeleton } from '../../../components/Skeleton'
import { Dropdown } from '../../../components/Dropdown'
import { CountInboxMembers } from '../CountInboxMembers'
import { DeleteInboxModal } from '../DeleteInboxModal'
import { LeaveInboxModal } from '../LeaveInboxModal'

// hooks
import { useInView } from '../../../hooks/useInView'

// handlers
import { useSourcesCountQuery } from '../../../requests/sources'
import { updateInbox } from '../../../requests/inboxes'
import { handleError } from '../../../errors/handleError'

import './styles.css'

type Props = {
  inbox: InboxType
  onLeaveInbox: (inboxRemoved: InboxType) => void
  onDeleteInbox: (inboxRemoved: InboxType) => void
  onInboxUpdated: (newInbox: InboxType) => void
  isOwner: boolean
}

export const OneInboxCard: React.FC<Props> = ({
  inbox,
  onDeleteInbox,
  onLeaveInbox,
  onInboxUpdated,
  isOwner
}) => {
  const [updating, setUpdating] = useState(false)
  const [isInView, setIsInView] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  // Memoize the date calculation to prevent rerendering loops
  // Set time to midnight so the cache key persists throughout the day
  const sevenDaysAgo = useMemo(() => {
    const now = new Date()
    const date = new Date(now)
    date.setDate(date.getDate() - 7)
    date.setHours(0, 0, 0, 0) // Set to midnight for consistent caching key
    return date
  }, [])

  const {
    data: sourcesCount = 0,
    isLoading: countingEmailsPerWeek
  } = useSourcesCountQuery(
    inbox._id,
    {
      startDate: sevenDaysAgo,
      ignore: false
    },
    {
      enabled: isInView, // Only fetch when card is in view
    }
  )

  useInView(cardRef, {
    rootMargin: '100px',
    triggerOnce: true,
    onIntersect: () => setIsInView(true)
  })

  const onToggleInboxPrivacy = async () => {
    if (updating) {
      return
    }

    setUpdating(true)

    try {
      const res = await updateInbox(inbox._id, {
        private: !inbox.private
      })

      onInboxUpdated(res.data)

      toast.success(`Folder is now ${res.data.private ? 'private' : 'public'} 👍`)

      setUpdating(false)
    } catch (err) {
      handleError(err, 'Failed to update folder privacy. Please try again later.')
    }
  }

  const dropdownActions = useMemo(() => {
    const actions = []
    
    if (isOwner) {
      actions.push(
        {
          label: 'View All Sources',
          href: `/app/folders/${inbox._id}/sources`
        },
        {
          label: `Make ${inbox.private ? 'Public' : 'Private'}`,
          onClick: onToggleInboxPrivacy,
          title: (
            inbox.private
              ? 'Click to allow anyone to join and view content of this folder'
              : 'Click to make this folder visible only to YOU'
          )
        },
        {
          label: 'Copy Email Address',
          onClick: async () => {
            await navigator.clipboard.writeText(inbox.email)
            toast.success('Email address copied to clipboard 👍')
          }
        },
        {
          label: 'Delete Folder',
          danger: true,
          onClick: () => onDeleteInbox(inbox)
        }
      )
    } else {
      actions.push({
        label: 'Remove Folder',
        danger: true,
        onClick: () => onLeaveInbox(inbox)
      })
    }

    return actions
  }, [isOwner, onLeaveInbox, onDeleteInbox, inbox])

  const handleCopyEmail = async () => {
    if (inbox.email) {
      try {
        await navigator.clipboard.writeText(inbox.email)
        toast.success('Email address copied to clipboard 👍')
      } catch (error) {
        handleError(error, 'Failed to copy email address')
      }
    }
  }

  return (
    <div ref={cardRef} className="one-inbox-card">
      <h2>{inbox.name}</h2>
      { !!inbox.email && (
        <button 
          className='email-line' 
          onClick={handleCopyEmail}
          title="Click to copy"
        >
          {inbox.email}
        </button>
      )}
      { isOwner && (
        <div className="owner-badge" title="You have full control over this folder.">
          <span>Owner</span>
        </div>
      )}
      { !isOwner && (
        <div className="member-badge" title="You can only view this folder's content.">
          <span>Member</span>
        </div>
      )}
      {!countingEmailsPerWeek && (
        <p className="info-line">
          {sourcesCount} new source{sourcesCount === 1 ? '' : 's'} a week
        </p>
      )}
      {countingEmailsPerWeek && (
        <p className="info-line">
          <Skeleton width={110} />
        </p>
      )}
      <p className="info-line">
        {inbox.private && 'Private Folder'}
        {!inbox.private && (<CountInboxMembers inboxId={inbox._id} />)}
      </p>

      <div className="inbox-card-footer">
        <Link to={`/app/content?inbox=${inbox._id}`}>
          <button className="secondary">
            View Content
          </button>
        </Link>
        <Dropdown items={dropdownActions} />
      </div>
    </div>
  )
}

export const OneInboxCardSkeleton: React.FC = () => {
  return (
    <div className="one-inbox-card">
      <h2><Skeleton width={240} /></h2>
      <p className='email-line'><Skeleton width={220} /></p>
      <p className="info-line">
        <Skeleton width={120} />
      </p>
      <p className="info-line">
        <Skeleton width={150} />
      </p>

      <div className="inbox-card-footer">
        <Skeleton width={40} />
      </div>
    </div>
  )
}


type ContainerProps = {
  inboxes: InboxType[]
  onInboxDeleted: (inboxRemoved: InboxType) => void
  onInboxUpdated: (newInbox: InboxType) => void
  isOwnerOfInbox: (inbox: InboxType) => boolean
  loading: boolean
}
export const InboxCardsContainer: React.FC<ContainerProps> = ({
  inboxes,
  onInboxDeleted,
  onInboxUpdated,
  isOwnerOfInbox,
  loading
}) => {
  const [inboxToLeave, setInboxToLeave] = useState<InboxType | null>(null)
  const [inboxToDelete, setInboxToDelete] = useState<InboxType | null>(null)

  inboxes.sort((a: InboxType, b: InboxType) => {
    if (isOwnerOfInbox(a)) {
      return -1
    }

    if (isOwnerOfInbox(b)) {
      return 1
    }

    return a.name.toLowerCase().localeCompare(b.name.toLowerCase())
  })

  if (loading) {
    return (
      <div className="inbox-cards-container">
        <OneInboxCardSkeleton />
        <OneInboxCardSkeleton />
        <OneInboxCardSkeleton />
        <OneInboxCardSkeleton />
        <OneInboxCardSkeleton />
        <OneInboxCardSkeleton />
      </div>
    )
  }
  return (
    <>
      <div className="inbox-cards-container">
        {
          inboxes.map((inbox) => (
            <OneInboxCard
              key={inbox._id}
              inbox={inbox}
              onLeaveInbox={setInboxToLeave}
              onDeleteInbox={setInboxToDelete}
              onInboxUpdated={onInboxUpdated}
              isOwner={isOwnerOfInbox(inbox)}
            />
          ))
        }
      </div>
      <LeaveInboxModal
        onClose={() => setInboxToLeave(null)}
        inboxToLeave={inboxToLeave}
        onLeftInbox={onInboxDeleted}
      />
      <DeleteInboxModal
        onClose={() => setInboxToDelete(null)}
        inboxToDelete={inboxToDelete}
        onInboxDeleted={onInboxDeleted}
      />
    </>
  )
}
